import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import {
  GET_MEMBERSHIP_DETAILS_EDUCATION_QUERY,
  GET_MEMBERSHIP_DETAILS_QUERY,
  GET_PROSPECT_PROGRAM_QUERY,
  GET_QUALIFYING_MEMBERSHIP_APPLICATIONS_QUERY
} from "../person/queries";
import { Person, ProgramStatus } from "@/types";
import { scrollToElement } from "@/lib/scrollHelpers";
import Banner from "@/components/Banner";
import ErrorBoundary from "@/components/ErrorBoundary";
import PATActivityTable from "@/features/person/components/PATActivityTable";
import PgmRequirementsSection, {
  buildQualifyingLevelRequirementsSectionProps
} from "@/features/person/components/PgmRequirementsSection";
import { canViewBackgroundCheckStaffSection } from "@/features/person/components/BackgroundCheckOrders/backgroundCheckEligibility";
import { getActiveSuspensionFromProgress, getSuspensionBannerConfig } from "@/features/person/utils/pgm-suspensions";
import {
  PAT_ACTIVITY_SCROLL_ID,
  isMembershipProgressScrollTarget
} from "@/features/person/utils/membershipProgressLinks";
import { DOMAIN, PERMISSION, useCanViewPartialBackgroundCheck, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { usePersonMembershipAccess } from "../person/_usePersonMembershipAccess";

type PersonData = { person: Person };

export default function ProspectProgramPage() {
  const { pgaId } = useParams();
  const { loaded, canViewPersonMembership } = usePersonMembershipAccess();
  const canViewPersonEducation = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonEducation
  });
  const canManageApplications = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });
  const [searchParams] = useSearchParams();
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const showProspectProgramTab = flagsLoaded && flags["backoffice-prospect-program-tab"] !== true;
  const canViewBackgroundCheck = useCanViewPartialBackgroundCheck();
  const staffSectionVisible = canViewBackgroundCheckStaffSection({
    flags,
    canViewPartialBackgroundCheck: canViewBackgroundCheck
  });

  const skipMembershipQueries = !loaded || !canViewPersonMembership || !pgaId || !showProspectProgramTab;

  const { data, loading, error } = useQuery<PersonData>(GET_MEMBERSHIP_DETAILS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: skipMembershipQueries
  });

  const {
    data: educationData,
    loading: educationLoading,
    error: educationError
  } = useQuery<PersonData>(GET_MEMBERSHIP_DETAILS_EDUCATION_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: skipMembershipQueries || !canViewPersonEducation
  });

  const {
    data: qualifyingLevelData,
    loading: qualifyingLevelLoading,
    error: qualifyingLevelError
  } = useQuery<PersonData>(GET_PROSPECT_PROGRAM_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: skipMembershipQueries
  });

  const {
    data: applicationsData,
    error: applicationsError,
    loading: applicationsLoading
  } = useQuery<PersonData>(GET_QUALIFYING_MEMBERSHIP_APPLICATIONS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: skipMembershipQueries || !canManageApplications
  });

  const membershipPerson = {
    ...(data?.person || ({} as Person)),
    ...(!educationError && educationData?.person ? educationData.person : {})
  };
  const membershipApplications = applicationsData?.person?.membershipApplications;
  const person: Person = {
    ...membershipPerson,
    prospectProgram: qualifyingLevelData?.person?.prospectProgram ?? membershipPerson.prospectProgram,
    accountCreatedAt: qualifyingLevelData?.person?.accountCreatedAt ?? membershipPerson.accountCreatedAt,
    ...(membershipApplications ? { membershipApplications } : {})
  };

  const prospectProgram = person.prospectProgram;
  const isLoading =
    qualifyingLevelLoading ||
    loading ||
    (canViewPersonEducation && educationLoading) ||
    (canManageApplications && applicationsLoading);

  const activeSuspension = person.pgmProgressStatus ? getActiveSuspensionFromProgress(person.pgmProgressStatus) : null;
  const suspensionBannerConfig = activeSuspension ? getSuspensionBannerConfig(activeSuspension.code) : null;
  const isSuspended = !!activeSuspension && person.latestProgram?.status !== ProgramStatus.TERMINATED;
  const canLinkApplications = canManageApplications && !applicationsLoading && !applicationsError;

  const qualifyingLevelSection = buildQualifyingLevelRequirementsSectionProps({
    person,
    pgaId: pgaId ?? "",
    staffSectionVisible,
    canLinkApplications,
    isSuspended,
    suspensionReason: suspensionBannerConfig?.description,
    terminationTimeRemaining: activeSuspension?.timeRemaining,
    loading: isLoading
  });

  useEffect(() => {
    if (!showProspectProgramTab || isLoading) return;

    const scrollTo = searchParams.get("scrollTo");
    if (!scrollTo || !isMembershipProgressScrollTarget(scrollTo)) return;

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("scrollTo");
    window.history.replaceState({}, "", currentUrl.toString());

    requestAnimationFrame(() => {
      scrollToElement(scrollTo);
    });
  }, [searchParams, isLoading, showProspectProgramTab]);

  if (!flagsLoaded) {
    return null;
  }

  if (!showProspectProgramTab && pgaId) {
    const query = searchParams.toString();
    return <Navigate to={`/people/${pgaId}/membership/path-to-membership${query ? `?${query}` : ""}`} replace />;
  }

  if (!loaded || !canViewPersonMembership) {
    return null;
  }

  if (error || qualifyingLevelError) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Prospect Program"
        message={error?.message || qualifyingLevelError?.message}
      />
    );
  }

  // Match Path to Membership: hold QL date metrics (and the rest of the page) until
  // person / QL / education / applications queries finish so staff never see a
  // Not Started → real dates flash from a partial person object.
  if (isLoading) {
    return (
      <div>
        <PgmRequirementsSection
          {...qualifyingLevelSection}
          dateMetrics={[]}
          requirements={[]}
          loading
          headingSize="default"
          topSpacing="default"
        />
        <div id={PAT_ACTIVITY_SCROLL_ID}>
          <PATActivityTable
            patStatus={undefined}
            loading
            enableNavigation={true}
            pgaId={pgaId}
            personId={person.id}
            person={person}
            pathAwareExpiration
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      {applicationsError && (
        <Banner
          variant="warn"
          description="Membership application links could not be loaded."
          subText="You can still review Prospect Program."
          testId="membership-applications-link-banner"
          hideSecondaryDismiss
          className="mt-8"
          inline
        />
      )}
      {educationError && (
        <Banner
          variant="warn"
          description="Education progress could not be loaded."
          subText="You can still review Prospect Program."
          testId="membership-education-progress-banner"
          hideSecondaryDismiss
          className="mt-8"
          inline
        />
      )}
      <PgmRequirementsSection {...qualifyingLevelSection} headingSize="default" topSpacing="default" />
      <div id={PAT_ACTIVITY_SCROLL_ID}>
        <PATActivityTable
          patStatus={prospectProgram?.playingAbilityTestStatus}
          loading={false}
          enableNavigation={true}
          pgaId={pgaId}
          personId={person.id}
          person={person}
          pathAwareExpiration
        />
      </div>
    </div>
  );
}
